/**
 * Pricing V3 Helper Functions
 * 用于处理 pricing v3 页面的价格查找和计算逻辑
 */

import {
  basicBasePrice,
  creditTiers,
  domainTiers,
  platinumBaseCredit,
  platinumBasePrice,
  premiumBaseCredit,
  premiumBasePrice,
} from './_const'
import {
  GetSubscriptionActionParams,
  PricingV3Params,
  SubscriptionAction,
} from './_types'

// 计划配置
const plans = {
  basic: {
    basePrice: basicBasePrice,
    creditOptions: creditTiers,
  },
  premium: {
    basePrice: premiumBasePrice,
    creditOptions: creditTiers.filter(
      (tier) => tier.value >= premiumBaseCredit,
    ),
  },
  platinum: {
    basePrice: platinumBasePrice,
    creditOptions: creditTiers.filter(
      (tier) => tier.value >= platinumBaseCredit,
    ),
  },
}

/**
 * 计算定价
 */
export function calculatePricingV3Price(params: PricingV3Params): number {
  const { plan, credits, domains, billingInterval } = params

  const planConfig = plans[plan]
  if (!planConfig) {
    throw new Error(`Invalid plan: ${plan}`)
  }

  const creditTier = creditTiers.find((tier) => tier.value === Number(credits))
  if (!creditTier) {
    throw new Error(`Invalid credits: ${credits}`)
  }
  if (
    !planConfig.creditOptions.find((tier) => tier.value === Number(credits))
  ) {
    throw new Error(`Unsupported credit tier: ${credits}`)
  }

  const domainOption = domainTiers.find(
    (option) => option.value === Number(domains),
  )
  if (!domainOption) {
    throw new Error(`Invalid domains: ${domains}`)
  }

  const monthlyPrice =
    planConfig.basePrice + creditTier.price + domainOption.price
  return billingInterval === 'yearly' ? monthlyPrice * 0.8 : monthlyPrice
}

/**
 * 验证参数是否有效
 */
export function validatePricingV3Params(params: PricingV3Params): boolean {
  const { plan, credits, domains, billingInterval } = params

  // 验证 plan
  if (!['basic', 'premium', 'platinum'].includes(plan)) {
    return false
  }

  // 验证 credits
  const validCredits = creditTiers.map((tier) => tier.value)
  if (!validCredits.includes(Number(credits))) {
    return false
  }

  // 验证 premium 计划的 credits 限制
  if (plan === 'premium' && Number.parseInt(credits) < premiumBaseCredit) {
    return false
  }

  // 验证 platinum 计划的 credits 限制
  if (plan === 'platinum' && Number.parseInt(credits) < platinumBaseCredit) {
    return false
  }

  // 验证 domains
  const validDomains = domainTiers.map((option) => option.value)
  if (!validDomains.includes(Number(domains))) {
    return false
  }

  // 验证 billingInterval
  if (!['monthly', 'yearly'].includes(billingInterval)) {
    return false
  }

  return true
}

/**
 * 比较两个计划是否为价格升级（用于API中的价格比较）
 * 这个函数专门用于API中需要实际价格比较的场景
 * @param currentParams 当前计划参数
 * @param newParams 新计划参数
 * @returns true 如果新计划价格更高，false 如果更低
 */
export function isPriceUpgrade(
  currentParams: PricingV3Params,
  newParams: PricingV3Params,
): boolean {
  const currentPrice = calculatePricingV3Price(currentParams)
  const newPrice = calculatePricingV3Price(newParams)

  return newPrice > currentPrice
}

/**
 * Determine the subscription action type
 * @returns The type of subscription action: new, current, upgrade, downgrade, or change
 */
export function getSubscriptionAction(
  params: GetSubscriptionActionParams,
): SubscriptionAction {
  const {
    subscription,
    currentPlan,
    targetPlan,
    targetCredits,
    targetDomains,
    selectedCredits,
    selectedDomains,
    isAnnual,
  } = params

  if (!subscription || currentPlan === 'free') {
    return 'new'
  }

  // TypeScript type guard: at this point currentPlan is 'basic' | 'premium' | 'platinum'
  const validPlan: 'basic' | 'premium' | 'platinum' = currentPlan

  const currentCredits = subscription.credits_per_month?.toString() || '200'
  const currentDomains = subscription.limits?.max_domains?.toString() || '1'

  if (currentPlan === targetPlan) {
    // 相同tier，检查配置是否相同
    const credits = targetCredits || selectedCredits[targetPlan]
    const domains = targetDomains || selectedDomains[targetPlan]

    // 检查billing cycle是否变更
    const currentBillingCycle = subscription?.billing_cycle || 'monthly'
    const targetBillingCycle = isAnnual ? 'yearly' : 'monthly'
    const isBillingCycleChange = currentBillingCycle !== targetBillingCycle

    if (
      credits === currentCredits &&
      domains === currentDomains &&
      !isBillingCycleChange
    ) {
      return 'current' // 完全相同的配置包括billing cycle
    }

    // 如果只是billing cycle变更（monthly ↔ yearly），视为升级（立即处理）
    if (
      credits === currentCredits &&
      domains === currentDomains &&
      isBillingCycleChange
    ) {
      return 'upgrade' // billing cycle变更视为升级，立即生效
    }

    // 相同tier但配置不同，判断价格变化
    const currentParams = {
      plan: validPlan,
      credits: currentCredits,
      domains: currentDomains,
      billingInterval: (subscription.billing_cycle || 'monthly') as
        | 'monthly'
        | 'yearly',
    }
    const targetParams = {
      plan: targetPlan,
      credits,
      domains,
      billingInterval: (isAnnual ? 'yearly' : 'monthly') as
        | 'monthly'
        | 'yearly',
    }

    if (isPriceUpgrade(currentParams, targetParams)) {
      return 'upgrade' // 价格增加视为升级
    }
    if (isPriceUpgrade(targetParams, currentParams)) {
      return 'downgrade' // 价格减少视为降级
    }
    return 'change' // 价格相同但配置不同
  }

  // 跨tier变更，基于价格判断而非tier名称
  const currentParams = {
    plan: validPlan,
    credits: currentCredits,
    domains: currentDomains,
    billingInterval: (subscription.billing_cycle || 'monthly') as
      | 'monthly'
      | 'yearly',
  }
  const targetParams = {
    plan: targetPlan,
    credits: targetCredits || selectedCredits[targetPlan],
    domains: targetDomains || selectedDomains[targetPlan],
    billingInterval: (isAnnual ? 'yearly' : 'monthly') as 'monthly' | 'yearly',
  }

  if (isPriceUpgrade(currentParams, targetParams)) {
    return 'upgrade' // 价格增加视为升级
  }
  if (isPriceUpgrade(targetParams, currentParams)) {
    return 'downgrade' // 价格减少视为降级
  }

  return 'change' // 价格相同但配置不同
}
